/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Build the conglomeration string from row data (same format as server):
 * each token in conglomerationFields is either "-" (append dash) or a field name (1 char, or 2 for colorCodes).
 */
export function buildConglomerationFromRow(row: any, conglomerationFields: string): string {
    if (!row || !conglomerationFields) {
        return '';
    }
    const tokens = conglomerationFields.split(',').map((t: string) => t.trim());
    if (tokens.length === 0) {
        return '';
    }
    const parts: string[] = [];
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === '-') {
            parts.push('-');
            continue;
        }
        const val = row[token];
        const charCount = (token.toLowerCase().includes('colorcode')) ? 2 : 1;
        // add 2 spaces, so there is always something to return when we call substring(0, len) later
        const s = val == null ? '  ' : String(val).trim() + '  ';
        parts.push(s.substring(0, charCount));
    }
    return parts.join('');
}

/**
 * Advance past one fixed-width slot reserved for a "-" token in the conglomeration string.
 * Always consumes one character when pos is in range, even if that character is not '-',
 * so read/write cursors stay aligned with conglomerationFields when the stored string is
 * missing or malformed at dash positions.
 */
function advancePastConglomerationDashSlot(str: string, pos: number): number {
    return pos < str.length ? pos + 1 : pos;
}

/**
 * Parse a conglomeration string into row fields: 1 char per field, except 2 for colorCode field; "-" consumes one dash.
 */
export function parseConglomerationToRow(row: any, conglomerationFields: string, value: string | null | undefined): void {
    if (!row || !conglomerationFields) {
        return;
    }
    const str = value == null ? '' : String(value);
    const tokens = conglomerationFields.split(',').map((t: string) => t.trim());
    if (tokens.length === 0) {
        return;
    }
    let pos = 0;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === '-') {
            pos = advancePastConglomerationDashSlot(str, pos);
            continue;
        }
        const isColorCode = (token.toLowerCase().includes('colorcode'));
        const len = isColorCode ? 2 : 1;
        const segment = (pos + len) <= str.length ? str.substring(pos, pos + len).trim() : '';
        pos += len;
        row[token] = segment;
    }
}

const TOKENS_CACHE = new Map<string, { tokens: string[]; twoCharIndices: Set<number> }>();

/** Cached token list and which field indices use 2 chars (e.g. colorCode). */
function getTokensAndTwoCharIndices(conglomerationFields: string): { tokens: string[]; twoCharIndices: Set<number> } {
    let cached = TOKENS_CACHE.get(conglomerationFields);
    if (cached) {
        return cached;
    }
    const tokens = conglomerationFields.split(',').map((t: string) => t.trim());
    const twoCharIndices = new Set<number>();
    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i] !== '-' && tokens[i].toLowerCase().includes('colorcode')) {
            twoCharIndices.add(i);
        }
    }
    cached = { tokens, twoCharIndices };
    TOKENS_CACHE.set(conglomerationFields, cached);
    return cached;
}

/**
 * Get the segment (1 or 2 chars) for a single field from the conglomeration string.
 */
export function getSegmentFromConglomeration(
    str: string,
    conglomerationFields: string,
    fieldName: string
): string {
    if (!str || !conglomerationFields) {
        return '';
    }
    const { tokens, twoCharIndices } = getTokensAndTwoCharIndices(conglomerationFields);
    let pos = 0;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === '-') {
            pos = advancePastConglomerationDashSlot(str, pos);
            continue;
        }
        const len = twoCharIndices.has(i) ? 2 : 1;
        if (token === fieldName) {
            return ((pos + len) <= str.length) ? str.substring(pos, pos + len).trim() : '';
        }
        pos += len;
    }
    return '';
}

/**
 * Return the conglomeration string with one field's segment replaced.
 * Value is formatted as 1 char or 2 chars (for colorCode fields), with padding as in buildConglomerationFromRow.
 */
export function setSegmentInConglomeration(
    str: string,
    conglomerationFields: string,
    fieldName: string,
    value: string | null | undefined
): string {
    if (!conglomerationFields) {
        return str;
    }
    const { tokens, twoCharIndices } = getTokensAndTwoCharIndices(conglomerationFields);
    let pos = 0;
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token === '-') {
            pos = advancePastConglomerationDashSlot(str, pos);
            continue;
        }
        const len = twoCharIndices.has(i) ? 2 : 1;
        if (token === fieldName) {
            // add 2 spaces, so there is always something to return when we call substring(0, len) later
            const s = (value == null) ? '  ' : String(value).trim() + '  ';
            const segment = s.substring(0, len);
            // add multiple spaces at the end, so there is something to return when we call substring(pos + len) later
            if (str.length < pos + len) {
                str += ' '.repeat(pos + len - str.length);
            }
            return str.substring(0, pos) + segment + str.substring(pos + len);
        }
        pos += len;
    }
    return str;
}

/**
 * Update only the segment for one constituent field in the row's conglomeration string.
 * Use this when a single constituent changes (e.g. dropdown) so the rest of the string is preserved.
 * Mutates row[conglomerationFieldName].
 */
export function updateConglomerationSegmentOnRow(
    row: any,
    conglomerationFieldName: string,
    conglomerationFields: string,
    fieldName: string,
    fieldValue: any
): void {
    if (!row || !conglomerationFields) {
        return;
    }
    const currentStr = row[conglomerationFieldName];
    const str = currentStr == null ? '' : String(currentStr).trim();
    const newStr = setSegmentInConglomeration(str, conglomerationFields, fieldName, fieldValue);
    row[conglomerationFieldName] = newStr;
}

/**
 * Return the set of field names (conglomeration + constituents) that are out of sync:
 * each constituent whose row value does not match its segment in the conglomeration string,
 * plus the conglomeration field if any constituent is out of sync.
 */
export function getOutOfSyncFieldsForConglomeration(
    row: any,
    conglomerationFieldName: string,
    conglomerationFields: string
): Set<string> {
    const result = new Set<string>();
    const str = row[conglomerationFieldName];
    const conglomerationStr = (str == null) ? '' : String(str).trim();
    const fieldNames = conglomerationFields.split(',')
                                           .map((t: string) => t.trim())
                                           .filter((t: string) => t !== '-');
    let anyOutOfSync = false;
    for (const fieldName of fieldNames) {
        if (fieldName === conglomerationFieldName) {
            continue;
        }
        const segment = getSegmentFromConglomeration(conglomerationStr, conglomerationFields, fieldName);
        const rowVal = row[fieldName];
        const rowStr = rowVal == null ? '' : String(rowVal).trim();
        if (segment !== rowStr) {
            result.add(fieldName);
            anyOutOfSync = true;
        }
    }
    if (anyOutOfSync) {
        result.add(conglomerationFieldName);
    }
    return result;
}
